import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Shield, X } from "lucide-react";

const NOTICE_KEY = "wg_notice_ack";

const hasAcknowledged = () => {
  try { return localStorage.getItem(NOTICE_KEY) === "1"; } catch { return false; }
};

const acknowledge = () => {
  try { localStorage.setItem(NOTICE_KEY, "1"); } catch { /* ignore */ }
};

function NoticeBody() {
  return (
    <>
      <p className="text-sm text-slate-600 leading-relaxed">
        Wir setzen ausschließlich technisch notwendige Speichertechniken ein. Analyse- oder Marketing-Dienste verwenden wir nicht,
        daher ist keine Einwilligung erforderlich. Mehr dazu in unserer{" "}
        <a href="/datenschutz" className="text-indigo-600 underline hover:text-indigo-800">Datenschutzerklärung</a>.
      </p>
      <div className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-indigo-200 bg-indigo-50/50" data-testid="cookie-cat-essential">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">Notwendig</span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Immer aktiv</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Anmeldung (Login-Token im Browser-Speicher), Zuordnung eines Empfehlungslinks (Empfehlungscode), Sicherheit und Speicherung dieses Hinweises. Ohne diese Funktionen kann der Dienst nicht genutzt werden.
          </p>
        </div>
      </div>
    </>
  );
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => { if (!hasAcknowledged()) setVisible(true); }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center p-4 pointer-events-none" data-testid="cookie-banner">
      <div className="pointer-events-auto bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-base font-bold text-slate-900" data-testid="cookie-title">Hinweis zu Cookies &amp; Speicherung</h2>
          </div>
          <NoticeBody />
        </div>
        <div className="px-6 pb-6">
          <Button
            data-testid="cookie-accept-necessary"
            onClick={() => { acknowledge(); setVisible(false); }}
            className="w-full h-11 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Floating button (desktop only) mit Popover – zeigt nur die notwendigen Techniken */
export function CookieSettingsButton() {
  const [open, setOpen] = useState(false);

  return (
    <div className="hidden sm:block fixed bottom-4 left-4 z-50">
      <div
        data-testid="cookie-popover"
        className={`absolute bottom-14 left-0 w-[400px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden origin-bottom-left transition-all duration-300 ease-out ${
          open ? "opacity-100 scale-100 translate-y-0 pointer-events-auto" : "opacity-0 scale-95 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="px-6 pt-6 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="flex-1 text-base font-bold text-slate-900">Cookies &amp; Speicherung</h3>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100" aria-label="Schließen">
              <X className="w-4 h-4" />
            </button>
          </div>
          <NoticeBody />
        </div>
      </div>

      <button
        data-testid="cookie-settings-btn"
        onClick={() => setOpen(!open)}
        title="Hinweis zu Cookies und Speicherung"
        className={`flex items-center gap-2 h-10 px-4 rounded-full bg-white shadow-lg border text-sm font-medium transition-all duration-200 hover:shadow-xl ${
          open ? "border-indigo-300 text-indigo-600" : "border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300"
        }`}
      >
        <Shield className="w-4 h-4" />
        Cookies
      </button>
    </div>
  );
}
